import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { Currency, useMutation } from '@sequencehq/api'
import {
  DateRangePickerField,
  GreyGrey70,
  IndigoIndigo60,
  InputSelectControlField,
  Link,
  Primary13Regular,
  useNotifications,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { CurrencyInputSelectControl } from 'components/CurrencyInputSelectControl/CurrencyInputSelectControl'
import { useXeroIntegration } from 'lib/hooks/useXeroIntegration'
import { useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../common/extractApiErrorMessage'
import { GENERATE_ANALYSIS_REPORT_COPY } from './generateAnalysisReport.constants'
import {
  isAnalysisReportFormValid,
  validateAnalysisReportForm
} from './generateAnalysisReport.validation'

const { modal, fields, notifications, docs, testIds } =
  GENERATE_ANALYSIS_REPORT_COPY

type Props = {
  trigger: React.ReactElement
}

export const GenerateAnalysisReportModal = ({ trigger }: Props) => {
  const { displayNotification } = useNotifications()
  const ledgerAccountOptions = useXeroLedgerAccountCodeOptions()

  const [currency, setCurrency] = useState<Currency | undefined>(undefined)
  const [xeroLedgerAccountCode, setXeroLedgerAccountCode] = useState<
    string | undefined
  >(undefined)
  const [dateRange, setDateRange] = useState<[Date, Date] | undefined>(
    undefined
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const postAnalysisReportMutation = useMutation(
    dashboardv99990101Client.postAnalysisReport
  )

  const errors = useMemo(
    () =>
      validateAnalysisReportForm({
        currency,
        xeroLedgerAccountCode,
        dateRange
      }),
    [currency, xeroLedgerAccountCode, dateRange]
  )

  const onSubmit = async () => {
    setShowValidationErrors(true)

    if (!isAnalysisReportFormValid(errors) || !currency || !dateRange) {
      return false
    }

    try {
      await postAnalysisReportMutation.mutateAsync({
        currency,
        xeroLedgerAccountCode: xeroLedgerAccountCode ?? '',
        from: apiDatesAdapters.toApi(dateRange[0]),
        to: apiDatesAdapters.toApi(dateRange[1])
      })
      displayNotification(notifications.success, { type: 'success' })
      return true
    } catch (e) {
      displayNotification(extractApiErrorMessage(e, notifications.error), {
        type: 'error'
      })
      return false
    }
  }

  return (
    <SimpleModal
      minHeight="0"
      title={modal.title}
      trigger={trigger}
      cancelButtonText={modal.cancelLabel}
      submitButtonText={modal.submitLabel}
      submittingButtonText={modal.submittingLabel}
      data-testid={testIds.modal}
      onSubmit={onSubmit}
    >
      <Flex flexDirection="column" gap="16px">
        <Box {...Primary13Regular} color={GreyGrey70}>
          {modal.body}{' '}
          <Link
            to={docs.href}
            target="_blank"
            rel="noopener noreferrer"
            color={IndigoIndigo60}
          >
            {docs.label}
          </Link>
        </Box>
        <CurrencyInputSelectControl
          data-testid={testIds.currency}
          label={fields.currency.label}
          placeholder={fields.currency.placeholder}
          value={currency ?? ''}
          onChange={setCurrency}
          validationErrors={toValidationErrors(
            showValidationErrors ? errors.currency : undefined
          )}
        />
        <InputSelectControlField
          data-testid={testIds.xeroLedgerAccountCode}
          label={fields.xeroLedgerAccountCode.label}
          placeholder={fields.xeroLedgerAccountCode.placeholder}
          value={xeroLedgerAccountCode ?? ''}
          onChange={setXeroLedgerAccountCode}
          options={ledgerAccountOptions}
          validationErrors={toValidationErrors(
            showValidationErrors ? errors.xeroLedgerAccountCode : undefined
          )}
        />
        <DateRangePickerField
          data-testid={testIds.dateRange}
          label={fields.dateRange.label}
          value={dateRange}
          onChange={setDateRange}
          validationErrors={toValidationErrors(
            showValidationErrors ? errors.dateRange : undefined
          )}
        />
      </Flex>
    </SimpleModal>
  )
}

const useXeroLedgerAccountCodeOptions = (): Array<{
  label: string
  value: string
}> => {
  const { xeroIntegration } = useXeroIntegration()

  return useMemo(
    () =>
      (xeroIntegration?.ledgerAccounts ?? []).map(({ code, name }) => ({
        label: `${code} – ${name}`,
        value: code
      })),
    [xeroIntegration?.ledgerAccounts]
  )
}

const toValidationErrors = (
  message: string | undefined
): Array<{ message: string }> | undefined =>
  message ? [{ message }] : undefined
